import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Trash2, AlertTriangle, Loader2 } from "lucide-react";

const DeleteDatabaseModal = ({
  isOpen,
  onClose,
  onConfirm,
  accountData,
  loading = false,
  message = null,
}) => {
  const [confirmationText, setConfirmationText] = useState("");
  const [error, setError] = useState(null);

  const expectedText = "DELETE";
  const isConfirmed = confirmationText === expectedText;

  const handleConfirm = () => {
    if (!isConfirmed) {
      setError("Please type 'DELETE' to confirm");
      return;
    }

    setError(null);
    onConfirm();
  };

  const handleClose = () => {
    setConfirmationText("");
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-[500px] bg-white border border-gray-200 shadow-xl text-gray-900"
        style={{ backgroundColor: "white", color: "#111827" }}
      >
        <DialogHeader className="bg-white text-gray-900">
          <DialogTitle className="flex items-center gap-2 text-red-600 font-semibold">
            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
              <Trash2 className="h-4 w-4 text-red-600" />
            </div>
            Delete Database
          </DialogTitle>
          <DialogDescription className="text-gray-700 text-sm">
            This action will permanently delete the database and all associated
            data. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 bg-white p-1 text-gray-900">
          {/* Success/Error Message */}
          {message && (
            <Alert
              className={`${
                message.type === "success"
                  ? "border-green-200 bg-green-50"
                  : "border-red-200 bg-red-50"
              }`}
            >
              <AlertDescription
                className={
                  message.type === "success" ? "text-green-800" : "text-red-800"
                }
              >
                {message.text}
              </AlertDescription>
            </Alert>
          )}

          {/* Account Information */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-3">Account Details</h4>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex justify-between">
                <span className="font-medium">Company:</span>
                <span>{accountData?.companyName || "N/A"}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Database:</span>
                <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                  {accountData?.name || "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Account Type:</span>
                <span className="capitalize">
                  {accountData?.accountType || "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Status:</span>
                <span className="capitalize">
                  {accountData?.status || "N/A"}
                </span>
              </div>
            </div>
          </div>

          {/* Warning Alert */}
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <div className="font-semibold mb-3">
                Warning: This will permanently delete:
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>
                    The entire tenant database{" "}
                    <code className="bg-red-100 px-1 rounded text-xs">
                      ({accountData?.name})
                    </code>
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>All user accounts in Auth0</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>The Auth0 organization</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>All tenant user records</span>
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-red-600 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>The database record from the system</span>
                </li>
              </ul>
            </AlertDescription>
          </Alert>

          {/* Confirmation Input */}
          <div className="space-y-3">
            <Label
              htmlFor="confirmation"
              className="text-sm font-medium text-gray-700"
            >
              To confirm deletion, type{" "}
              <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                DELETE
              </code>{" "}
              below:
            </Label>
            <Input
              id="confirmation"
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="font-mono text-center text-lg tracking-wider"
              disabled={loading}
            />
            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200">
                {error}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex justify-end gap-3 pt-4 bg-white text-gray-900">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
            className="px-6"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isConfirmed || loading}
            className="bg-red-600 hover:bg-red-700 px-6"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Database
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteDatabaseModal;
